import { Router } from "express";
import { db } from "@workspace/db";
import { chatMessagesTable, tendersTable, analysesTable, tenderDocumentsTable } from "@workspace/db";
import { eq, and, asc, desc } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { chatWithTender } from "../lib/openai";
import { chatWithVectorStore } from "../lib/vectorStore.js";

const router = Router();

router.get("/tenders/:id/chat", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db
      .select({ id: tendersTable.id })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const messages = await db
      .select()
      .from(chatMessagesTable)
      .where(eq(chatMessagesTable.tenderId, tenderId))
      .orderBy(asc(chatMessagesTable.createdAt));
    res.json(messages);
  } catch (err) {
    next(err);
  }
});

router.post("/tenders/:id/chat", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const { message, language = "ar" } = req.body;
    const companyId = req.companyId;

    const tender = await db
      .select()
      .from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId!)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    // Save user message
    await db.insert(chatMessagesTable).values({
      tenderId,
      userId: req.dbUser!.id,
      role: "user",
      content: message,
    });

    // Fetch full history (including the just-saved message)
    const history = await db
      .select()
      .from(chatMessagesTable)
      .where(eq(chatMessagesTable.tenderId, tenderId))
      .orderBy(asc(chatMessagesTable.createdAt))
      .limit(30);

    const historyMsgs = history.map((m) => ({
      role: m.role as "user" | "assistant",
      content: m.content,
    }));

    let aiReply: string;
    const vectorStoreId = tender[0].vectorStoreId;

    if (vectorStoreId) {
      // ── RAG path: Responses API with file_search ──────────────────────────
      aiReply = await chatWithVectorStore(
        vectorStoreId,
        historyMsgs,
        language as "ar" | "en",
        tender[0].title,
      );
    } else {
      // ── Fallback: build context from extracted text + analysis ────────────
      const [analysis, docs] = await Promise.all([
        db.select().from(analysesTable)
          .where(eq(analysesTable.tenderId, tenderId))
          .orderBy(desc(analysesTable.createdAt))
          .limit(1),
        db.select().from(tenderDocumentsTable)
          .where(eq(tenderDocumentsTable.tenderId, tenderId))
          .orderBy(desc(tenderDocumentsTable.createdAt))
          .limit(3),
      ]);

      const docsText = docs
        .filter((d) => d.extractedText)
        .map((d) => d.extractedText!)
        .join("\n\n---\n\n")
        .slice(0, 4000);

      const tenderContext = [
        `Title: ${tender[0].title}`,
        `Description: ${tender[0].description || "N/A"}`,
        analysis[0] ? `Analysis: ${JSON.stringify(analysis[0]).slice(0, 2500)}` : "",
        docsText ? `Document Content:\n${docsText}` : "",
      ].filter(Boolean).join("\n\n");

      aiReply = await chatWithTender(historyMsgs, tenderContext, language as "ar" | "en");
    }

    const saved = await db
      .insert(chatMessagesTable)
      .values({ tenderId, role: "assistant", content: aiReply })
      .returning();

    // Return the message with a flag indicating if VS was used
    res.json({ ...saved[0], usedVectorStore: !!vectorStoreId });
  } catch (err) {
    next(err);
  }
});

// ── GET tender vector store status ─────────────────────────────────────────
router.get("/tenders/:id/vector-store-status", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db
      .select({ id: tendersTable.id, vectorStoreId: tendersTable.vectorStoreId })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const docs = await db
      .select({ id: tenderDocumentsTable.id, openaiFileId: tenderDocumentsTable.openaiFileId })
      .from(tenderDocumentsTable)
      .where(eq(tenderDocumentsTable.tenderId, tenderId));

    const indexedCount = docs.filter((d) => d.openaiFileId).length;

    res.json({
      vectorStoreId: tender[0].vectorStoreId,
      hasVectorStore: !!tender[0].vectorStoreId,
      totalDocs: docs.length,
      indexedDocs: indexedCount,
    });
  } catch (err) {
    next(err);
  }
});

export default router;
